/**
 * Hero Which can display slides
 *
 * features
 * - image slide
 * - videos (youtube/vimeo which hide beneath the slide until activated)
 *
 * USES LIB YOUTUBE PLAYER - https://www.npmjs.com/package/youtube-player#usage
 * USES LIB VIMEO PLAYER   - https://www.npmjs.com/package/@vimeo/player
 */

import YouTubePlayer from 'youtube-player';
import VimeoPlayer from '@vimeo/player';
import Glide from '@glidejs/glide';

// Stored outside Alpine so instances are never wrapped in a reactive Proxy.
// Vimeo's SDK uses a WeakMap keyed on the raw player instance; if Alpine
// proxies the object, readyMap.get(proxy) returns undefined → "Unknown player".
const _vimeoPlayers = new Map(); // videoId (string) → VimeoPlayer instance

export const B__Hero = () => ({
    expanded: false,
    player: null,
    glide: null,
    players: [],
    overlayIsActive: false,
    isPlaying: false,
    isMuted: false,
    controlsVisible: true,
    _controlsTimer: null,
    currentSlide: 0,
    currentTheme: 'dark',
    currentVideoTheme: 'dark',
    glideIndex: 0,
    v: null, // ACF repeater values from WP passed from template

    async initializeWithTemplateData(v) {
        this.v = JSON.parse(v);

        const Module = this;

        /**
         * AlpineTemplateLogicIssueFix
         * // since alpine creates a DOM <template x-for...> as an extra sibling to the li elements
         * // we need to remove it
         */
        const AlpineTemplateLogicIssueFix = function (
            Glide,
            Components,
            Events
        ) {
            return {
                modify(translate, current, slides) {
                    if (
                        Components.Html.slides.length > Module.v.slides.length
                    ) {
                        Components.Html.slides.shift();
                    }
                    return translate;
                }
            };
        };

        setTimeout(() => {
            const glide = new Glide('.glide');

            glide.on(['build.before'], function () {
                if (Module.v.slides) {
                    Module.v.slides.map((item, index) => {
                        if (item.type == 'video')
                            Module.initializeYoutubeVideo(item, index);
                    });
                }
            });

            glide.on(['run'], function () {
                if (glide.index == Module.v.slides.length - 1) {
                    // console.log('DO NOT change SLIDE');
                }
            });

            glide.on(['mount.before', 'run'], function () {
                // Pause any playing video before leaving the current slide
                if (Module.overlayIsActive && Module.currentSlide) {
                    if (
                        Module.currentSlide.type == 'vimeo' &&
                        Module.currentSlide.vimeoVideoId
                    ) {
                        const leavingPlayer = Module.findVimeoPlayer(
                            Module.currentSlide.vimeoVideoId
                        );
                        if (leavingPlayer) leavingPlayer.pause();
                    } else if (Module.currentSlide.youtubeVideoId) {
                        const leavingPlayer = Module.findPlayerByYoutubeId(
                            Module.currentSlide.youtubeVideoId
                        );
                        if (leavingPlayer) leavingPlayer.api.pauseVideo();
                    }
                }

                Module.glideIndex = glide.index;
                Module.setCurrentSlide(Module.getSlideObject(glide.index));

                Module.overlayIsActive = false;
                Module.isPlaying = false;
                Module.isMuted = false;
                clearTimeout(Module._controlsTimer);
                Module.controlsVisible = true;
                Module.autoplayCurrentSlide();
            });

            glide.on(['mount.after'], function () {
                // Init Vimeo here — after Glide has finished all DOM work —
                // so the player element is in its final position in the DOM.
                if (Module.v.slides) {
                    Module.v.slides.forEach((item, index) => {
                        if (item.type == 'vimeo')
                            Module.initializeVimeoVideo(item, index);
                    });
                }
                Module.autoplayCurrentSlide();
            });

            // apply template fix (alpine + glide collision)
            this.glide = glide.mutate([AlpineTemplateLogicIssueFix]).mount();
        }, 100);

        this.$watch('overlayIsActive', (value) => {
            document.body.classList.toggle('playback-mode', value);
        });
    },

    changeVolume(player, i) {
        if (!player) return;

        if (i) {
            player.api.getVolume().then((volume) => {
                player.api.setVolume(volume + 1);
            });
        } else {
            player.api.getVolume().then((volume) => {
                player.api.setVolume(volume - 1);
            });
        }
    },

    getSlideObject(index) {
        return this.v.slides[index];
    },

    setCurrentSlide(slide) {
        this.currentSlide = slide;
        this.currentTheme = slide?.theme || 'dark';
        this.currentVideoTheme = slide?.videoTheme || 'dark';
    },

    findPlayerByYoutubeId(id) {
        return this.players.find((player) => player.id == id);
    },

    findVimeoPlayer(id) {
        return _vimeoPlayers.get(String(id));
    },

    initializeYoutubeVideo(slide, index) {
        if (!slide.youtubeVideoId) return;

        const elemId = 'player_' + index + '_' + slide.youtubeVideoId;

        const yp = YouTubePlayer(elemId, {
            videoId: slide.youtubeVideoId,
            playerVars: {
                controls: 0,
                modestbranding: 1,
                autoplay: 0,
                rel: 0,
                origin: window.location.origin,
                enablejsapi: 1
            }
        });

        this.players.push({
            id: slide.youtubeVideoId,
            api: yp
        });

        let player = this.findPlayerByYoutubeId(slide.youtubeVideoId);

        player.api.on('stateChange', (event) => {
            this.maybyOverlayIsActive(event.data);
        });
    },

    initializeVimeoVideo(slide, index) {
        if (!slide.vimeoVideoId) return;

        const elemId = 'vimeo_' + index + '_' + slide.vimeoVideoId;

        const vp = new VimeoPlayer(elemId, {
            id: parseInt(slide.vimeoVideoId),
            controls: false,
            autoplay: false,
            muted: false
        });

        _vimeoPlayers.set(String(slide.vimeoVideoId), vp);

        vp.on('play', () => this.maybyOverlayIsActive(1));
        vp.on('pause', () => this.maybyOverlayIsActive(2));
        vp.on('ended', () => this.maybyOverlayIsActive(0));
    },

    fadeOut() {
        let player = this.findPlayerByYoutubeId(
            this.currentSlide.youtubeVideoId
        );

        if (!player) return;

        let fadeOutTimerId = setInterval(() => this.changeVolume(player, 0), 2);

        setTimeout(() => {
            clearInterval(fadeOutTimerId);
        }, 400);
    },
    fadeIn() {
        let player = this.findPlayerByYoutubeId(
            this.currentSlide.youtubeVideoId
        );

        if (!player) return;

        let fadeInTimerId = setInterval(() => this.changeVolume(player, 1), 2);

        setTimeout(() => {
            clearInterval(fadeInTimerId);
        }, 400);
    },

    autoplayCurrentSlide() {
        if (!this.currentSlide) return;
        if (
            this.currentSlide.type !== 'video' &&
            this.currentSlide.type !== 'vimeo'
        )
            return;
        if (!this.currentSlide.autoplay) return;

        if (this.currentSlide.type == 'vimeo') {
            const vp = this.findVimeoPlayer(this.currentSlide.vimeoVideoId);
            if (!vp) return;
            vp.ready()
                .then(() => vp.setMuted(true))
                .then(() => vp.play());
        } else {
            const player = this.findPlayerByYoutubeId(
                this.currentSlide.youtubeVideoId
            );
            if (!player) return;
            player.api.mute();
            player.api.playVideo();
        }
        this.isMuted = true;
        this.isPlaying = true;
        this.overlayIsActive = true;
        this.showControls();
    },

    toggleMute() {
        if (this.currentSlide.type == 'vimeo') {
            const vp = this.findVimeoPlayer(this.currentSlide.vimeoVideoId);
            if (!vp) return;
            const newMuted = !this.isMuted;
            vp.setMuted(newMuted).then(() => {
                this.isMuted = newMuted;
            });
        } else {
            const player = this.findPlayerByYoutubeId(
                this.currentSlide.youtubeVideoId
            );
            if (!player) return;
            if (this.isMuted) {
                player.api.unMute();
                this.isMuted = false;
            } else {
                player.api.mute();
                this.isMuted = true;
            }
        }
    },

    showControls() {
        if (!this.overlayIsActive) return;
        this.controlsVisible = true;
        clearTimeout(this._controlsTimer);
        this._controlsTimer = setTimeout(() => {
            this.controlsVisible = false;
        }, 2000);
    },

    hideControls() {
        if (!this.overlayIsActive) return;
        clearTimeout(this._controlsTimer);
        this.controlsVisible = false;
    },

    pauseVideo() {
        if (this.currentSlide.type == 'vimeo') {
            const vp = this.findVimeoPlayer(this.currentSlide.vimeoVideoId);
            if (!vp) return;
            vp.pause();
        } else {
            const player = this.findPlayerByYoutubeId(
                this.currentSlide.youtubeVideoId
            );
            if (!player) return;
            player.api.pauseVideo();
        }
        this.isPlaying = false;
    },

    resumeVideo() {
        if (this.currentSlide.type == 'vimeo') {
            const vp = this.findVimeoPlayer(this.currentSlide.vimeoVideoId);
            if (!vp) return;
            vp.play();
        } else {
            const player = this.findPlayerByYoutubeId(
                this.currentSlide.youtubeVideoId
            );
            if (!player) return;
            player.api.playVideo();
        }
        this.isPlaying = true;
    },

    enterFullscreen() {
        const el = this.$root;
        if (el && el.requestFullscreen) el.requestFullscreen();
    },

    stopVideo() {
        if (this.currentSlide.type == 'vimeo') {
            const vp = this.findVimeoPlayer(this.currentSlide.vimeoVideoId);
            setTimeout(() => {
                if (vp) vp.pause();
                this.overlayIsActive = false;
                this.isPlaying = false;
                this.isMuted = false;
                clearTimeout(this._controlsTimer);
                this.controlsVisible = true;
            }, 400);
        } else {
            const player = this.findPlayerByYoutubeId(
                this.currentSlide.youtubeVideoId
            );
            setTimeout(() => {
                if (player) player.api.pauseVideo();
                this.overlayIsActive = false;
                this.isPlaying = false;
                this.isMuted = false;
                clearTimeout(this._controlsTimer);
                this.controlsVisible = true;
            }, 400);
        }
    },

    playVideo() {
        if (this.currentSlide.type == 'vimeo') {
            const vp = this.findVimeoPlayer(this.currentSlide.vimeoVideoId);
            if (!vp) return;
            vp.play();
        } else {
            const player = this.findPlayerByYoutubeId(
                this.currentSlide.youtubeVideoId
            );
            if (!player) return;
            player.api.playVideo();
        }
        // isPlaying is intentionally NOT set here — it is set by maybyOverlayIsActive
        // when the player confirms playback (state=1 / play event). This prevents
        // the poster overlay from fading before YouTube's own play button disappears.
        this.overlayIsActive = true;
        this.showControls();
    },

    maybyOverlayIsActive(playerStatus) {
        if (playerStatus == 0) {
            // Video ended — reset immediately so end-screen never renders
            this.overlayIsActive = false;
            this.isPlaying = false;
            this.isMuted = false;
        } else if (playerStatus == 1) {
            this.overlayIsActive = true;
            this.isPlaying = true;
        } else if (playerStatus == 2) {
            this.overlayIsActive = false;
            this.isPlaying = false;
            this.isMuted = false;
            clearTimeout(this._controlsTimer);
            this.controlsVisible = true;
        }
    }
});
